// layouts/profile/components/PlatformSettings/index.js
import { useEffect, useMemo, useState } from "react";

// MUI
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";

// Soft UI
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

/** Etiquetas legibles */
const LABELS = {
  users: "Usuarios",
  groups: "Grupos",
  sellos: "Sellos",
  generos: "Géneros",
  reconocimientos: "Reconocimientos",
  nacionalidades: "Nacionalidades",
  certificaciones: "Certificaciones",
  reportes: "Reportes",
  parametros: "Parámetros",
};

/** Estructura por defecto (por si no llegan permisos todavía) */
const DEFAULT_PERMS = {
  users:              { view: false, create: false, edit: false, delete: false },
  groups:             { view: false, create: false, edit: false, delete: false },
  sellos:             { view: false, create: false, edit: false, delete: false },
  generos:            { view: false, create: false, edit: false, delete: false },
  reconocimientos:    { view: false, create: false, edit: false, delete: false },
  nacionalidades:     { view: false, create: false, edit: false, delete: false },
  certificaciones:    { view: false, create: false, edit: false, delete: false },
  reportes:           { view: false, create: false, edit: false, delete: false },
  dashboard:          { view: false, create: false, edit: false, delete: false },
  parametros:         { view: false, create: false, edit: false, delete: false },
};

/** Mezcla con defaults para evitar claves faltantes */
function withDefaults(perms) {
  const base = { ...DEFAULT_PERMS };
  const incoming = perms && typeof perms === "object" ? perms : {};
  for (const mod of Object.keys(base)) {
    base[mod] = { ...base[mod], ...(incoming[mod] || {}) };
  }
  return base;
}

/**
 * PlatformSettings
 * props:
 * - isAdmin: boolean     -> habilita edición
 * - permissions: object  -> estructura { modulo: {view,create,edit,delete} }
 * - onSave(perms): fn    -> callback al guardar (opcional)
 * - onChange(perms): fn  -> callback en cada cambio (opcional)
 */
export default function PlatformSettings({
  isAdmin = false,
  permissions,
  onSave,
  onChange,
}) {
  // Fallback de prueba: localStorage.debug_perms (útil mientras el backend se actualiza)
  const permsFromLS = useMemo(() => {
    try {
      const raw = localStorage.getItem("debug_perms");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const [localPerms, setLocalPerms] = useState(
    withDefaults(permissions || permsFromLS || DEFAULT_PERMS)
  );

  // Si cambian los permisos desde fuera, sincroniza estado
  useEffect(() => {
    setLocalPerms(withDefaults(permissions || permsFromLS || DEFAULT_PERMS));
  }, [permissions, permsFromLS]);

  const handleToggle = (mod, key) => {
    if (!isAdmin) return; // solo admin edita
    setLocalPerms((prev) => {
      const next = {
        ...prev,
        [mod]: { ...prev[mod], [key]: !prev[mod][key] },
      };
      onChange?.(next);
      return next;
    });
  };

  const handleReset = () => {
    const reset = withDefaults(permissions || DEFAULT_PERMS);
    setLocalPerms(reset);
    onChange?.(reset);
  };

  const handleSave = () => {
    onSave?.(localPerms);
  };

  // Qué módulos mostramos:
  // - Admin: todos
  // - No admin: solo los que tengan view=true
  const modulesToShow = useMemo(() => {
    const entries = Object.entries(localPerms);
    if (isAdmin) return entries;
    return entries.filter(([_, p]) => p?.view);
  }, [localPerms, isAdmin]);

  return (
    <Card>
      <SoftBox pt={2} px={2} pb={0.5}>
        <SoftTypography variant="h6" fontWeight="medium" textTransform="capitalize">
          Permisos
        </SoftTypography>
        {!isAdmin && (
          <SoftTypography variant="caption" color="text">
            Solo lectura (mostrando únicamente los módulos con acceso).
          </SoftTypography>
        )}
      </SoftBox>

      <SoftBox px={2} pb={2}>
        {/* Encabezados */}
        <Grid container spacing={1} sx={{ opacity: 0.7, mb: 1 }}>
          <Grid item xs={12} sm={4}>
            <SoftTypography variant="caption" fontWeight="bold">Módulo</SoftTypography>
          </Grid>
          <Grid item xs={3} sm={2}>
            <SoftTypography variant="caption" fontWeight="bold">Ver</SoftTypography>
          </Grid>
          <Grid item xs={3} sm={2}>
            <SoftTypography variant="caption" fontWeight="bold">Crear</SoftTypography>
          </Grid>
          <Grid item xs={3} sm={2}>
            <SoftTypography variant="caption" fontWeight="bold">Editar</SoftTypography>
          </Grid>
          <Grid item xs={3} sm={2}>
            <SoftTypography variant="caption" fontWeight="bold">Eliminar</SoftTypography>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 1 }} />

        {/* Filas por módulo */}
        {modulesToShow.map(([mod, p]) => (
          <SoftBox key={mod} py={0.75}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} sm={4}>
                <SoftTypography variant="button">{LABELS[mod] || mod}</SoftTypography>
              </Grid>

              <Grid item xs={3} sm={2}>
                <Switch
                  checked={!!p.view}
                  onChange={() => handleToggle(mod, "view")}
                  disabled={!isAdmin}
                />
              </Grid>
              <Grid item xs={3} sm={2}>
                <Switch
                  checked={!!p.create}
                  onChange={() => handleToggle(mod, "create")}
                  disabled={!isAdmin}
                />
              </Grid>
              <Grid item xs={3} sm={2}>
                <Switch
                  checked={!!p.edit}
                  onChange={() => handleToggle(mod, "edit")}
                  disabled={!isAdmin}
                />
              </Grid>
              <Grid item xs={3} sm={2}>
                <Switch
                  checked={!!p.delete}
                  onChange={() => handleToggle(mod, "delete")}
                  disabled={!isAdmin}
                />
              </Grid>
            </Grid>
          </SoftBox>
        ))}

        {/* Acciones solo para admin */}
        {isAdmin && (
          <SoftBox mt={1.5} display="flex" gap={1}>
            <SoftButton variant="outlined" color="secondary" onClick={handleReset}>
              Restaurar
            </SoftButton>
            <SoftButton color="dark" variant="gradient" onClick={handleSave}>
              Guardar cambios
            </SoftButton>
          </SoftBox>
        )}
      </SoftBox>
    </Card>
  );
}
